import React, { useState } from 'react';
import { Form as FinalForm } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import { propTypes } from '../../../util/types';
import * as validators from '../../../util/validators';
import { getPropsForCustomUserFieldInputs } from '../../../util/userHelpers';
import { US_STATES } from '../../../util/usStates';

import { Form, PrimaryButton, FieldTextInput, FieldSelect, CustomExtendedDataField } from '../../../components';

import FieldSelectUserType from '../FieldSelectUserType';
import UserFieldDisplayName from '../UserFieldDisplayName';
import UserFieldPhoneNumber from '../UserFieldPhoneNumber';

import css from './SignupForm.module.css';

const getSoleUserTypeMaybe = userTypes =>
  Array.isArray(userTypes) && userTypes.length === 1 ? userTypes[0].userType : null;

const isPasswordUsedMoreThanOnce = formValues => {
  const pw = formValues.password;
  const hasPasswordString = pw != null && pw.length >= validators.PASSWORD_MIN_LENGTH;
  if (hasPasswordString) {
    // Exclude password + confirmPassword fields — confirmPassword intentionally matches password
    const { password, confirmPassword, ...otherValues } = formValues; // eslint-disable-line no-unused-vars
    const isPasswordRepeated = Object.values(otherValues).filter(v => v === pw).length > 0;
    return isPasswordRepeated;
  }
  return false;
};

// Step 1 fields that must pass validation before advancing
const STEP1_FIELDS = ['fname', 'lname', 'email', 'password', 'confirmPassword'];

const SignupFormComponent = props => {
  const [step, setStep] = useState(1);

  return (
    <FinalForm
      {...props}
      mutators={{ ...arrayMutators }}
      initialValues={{
        userType: props.preselectedUserType || getSoleUserTypeMaybe(props.userTypes),
        country: 'United States',
      }}
      render={formRenderProps => {
        const {
          rootClassName,
          className,
          formId,
          handleSubmit,
          inProgress,
          invalid,
          intl,
          termsAndConditions,
          preselectedUserType,
          userTypes,
          userFields,
          values,
          form,
        } = formRenderProps;

        const { userType } = values || {};

        // ── Step 1 validators ─────────────────────────────────────────────────

        const emailRequired = validators.required(
          intl.formatMessage({ id: 'SignupForm.emailRequired' })
        );
        const emailValid = validators.emailFormatValid(
          intl.formatMessage({ id: 'SignupForm.emailInvalid' })
        );

        const passwordMinLengthMessage = intl.formatMessage(
          { id: 'SignupForm.passwordTooShort' },
          { minLength: validators.PASSWORD_MIN_LENGTH }
        );
        const passwordMaxLengthMessage = intl.formatMessage(
          { id: 'SignupForm.passwordTooLong' },
          { maxLength: validators.PASSWORD_MAX_LENGTH }
        );
        const passwordValidators = validators.composeValidators(
          validators.requiredStringNoTrim(intl.formatMessage({ id: 'SignupForm.passwordRequired' })),
          validators.minLength(passwordMinLengthMessage, validators.PASSWORD_MIN_LENGTH),
          validators.maxLength(passwordMaxLengthMessage, validators.PASSWORD_MAX_LENGTH)
        );

        // ── Custom user fields ────────────────────────────────────────────────

        const userFieldProps = getPropsForCustomUserFieldInputs(userFields, userType);
        const noUserTypes = !userType && !(userTypes?.length > 0);
        const userTypeConfig = userTypes.find(config => config.userType === userType);
        const showDefaultUserFields = userType || noUserTypes;
        const showCustomUserFields = (userType || noUserTypes) && userFieldProps?.length > 0;

        const classes = classNames(rootClassName || css.root, className);
        const submitInProgress = inProgress;
        const submitDisabled = invalid || submitInProgress || isPasswordUsedMoreThanOnce(values);

        // ── NEXT handler: validate step 1 before advancing ────────────────────
        const handleNext = () => {
          STEP1_FIELDS.forEach(field => form.blur(field));
          const { errors } = form.getState();
          const hasErrors = STEP1_FIELDS.some(field => errors && errors[field]);
          if (!hasErrors) {
            setStep(2);
            window.scrollTo(0, 0);
          }
        };

        // ── STEP 1 ────────────────────────────────────────────────────────────
        if (step === 1) {
          return (
            <Form className={classes} onSubmit={e => e.preventDefault()}>
              <FieldSelectUserType
                name="userType"
                userTypes={userTypes}
                hasExistingUserType={!!preselectedUserType}
                intl={intl}
              />

              {showDefaultUserFields ? (
                <div className={css.defaultUserFields}>
                  <div className={css.name}>
                    <FieldTextInput
                      className={css.firstNameRoot}
                      type="text"
                      id={formId ? `${formId}.fname` : 'fname'}
                      name="fname"
                      autoComplete="given-name"
                      label={intl.formatMessage({ id: 'SignupForm.firstNameLabel' })}
                      placeholder={intl.formatMessage({ id: 'SignupForm.firstNamePlaceholder' })}
                      validate={validators.required(
                        intl.formatMessage({ id: 'SignupForm.firstNameRequired' })
                      )}
                    />
                    <FieldTextInput
                      className={css.lastNameRoot}
                      type="text"
                      id={formId ? `${formId}.lname` : 'lname'}
                      name="lname"
                      autoComplete="family-name"
                      label={intl.formatMessage({ id: 'SignupForm.lastNameLabel' })}
                      placeholder={intl.formatMessage({ id: 'SignupForm.lastNamePlaceholder' })}
                      validate={validators.required(
                        intl.formatMessage({ id: 'SignupForm.lastNameRequired' })
                      )}
                    />
                  </div>

                  <FieldTextInput
                    className={css.row}
                    type="email"
                    id={formId ? `${formId}.email` : 'email'}
                    name="email"
                    autoComplete="email"
                    label={intl.formatMessage({ id: 'SignupForm.emailLabel' })}
                    placeholder={intl.formatMessage({ id: 'SignupForm.emailPlaceholder' })}
                    validate={validators.composeValidators(emailRequired, emailValid)}
                  />

                  <UserFieldDisplayName
                    formName="SignupForm"
                    className={css.row}
                    userTypeConfig={userTypeConfig}
                    intl={intl}
                  />

                  <FieldTextInput
                    className={css.password}
                    type="password"
                    id={formId ? `${formId}.password` : 'password'}
                    name="password"
                    autoComplete="new-password"
                    label={intl.formatMessage({ id: 'SignupForm.passwordLabel' })}
                    placeholder={intl.formatMessage({ id: 'SignupForm.passwordPlaceholder' })}
                    validate={passwordValidators}
                  />

                  <FieldTextInput
                    className={css.confirmPassword}
                    type="password"
                    id={formId ? `${formId}.confirmPassword` : 'confirmPassword'}
                    name="confirmPassword"
                    autoComplete="new-password"
                    label="Confirm Password"
                    placeholder=""
                    validate={value => {
                      if (!value) return 'Please confirm your password';
                      if (value !== values.password) return 'Passwords do not match';
                      return undefined;
                    }}
                  />
                </div>
              ) : null}

              {isPasswordUsedMoreThanOnce(values) ? (
                <div className={css.error}>
                  <FormattedMessage id="SignupForm.passwordRepeatedOnOtherFields" />
                </div>
              ) : null}

              <div className={css.bottomWrapper}>
                <button type="button" className={css.nextBtn} onClick={handleNext}>
                  Next
                </button>
              </div>
            </Form>
          );
        }

        // ── STEP 2 ────────────────────────────────────────────────────────────
        return (
          <Form className={classes} onSubmit={handleSubmit}>
            <div className={css.step2Header}>
              <p className={css.step2Label}>Your Personal Information</p>
              <p className={css.step2Desc}>
                We need this to ship your items and to send you a prepaid shipping label when your
                item sells.
              </p>
            </div>

            <FieldTextInput
              className={css.row}
              type="text"
              id={formId ? `${formId}.streetAddress` : 'streetAddress'}
              name="streetAddress"
              autoComplete="street-address"
              label="Street Address"
              placeholder=""
              validate={validators.required('Street address is required')}
            />

            <FieldTextInput
              className={css.row}
              type="text"
              id={formId ? `${formId}.unitApt` : 'unitApt'}
              name="unitApt"
              autoComplete="address-line2"
              label="Unit / Apt (Optional)"
              placeholder=""
            />

            <div className={css.addressRow}>
              <FieldTextInput
                className={css.halfField}
                type="text"
                id={formId ? `${formId}.city` : 'city'}
                name="city"
                autoComplete="address-level2"
                label="City"
                placeholder=""
                validate={validators.required('City is required')}
              />
              <FieldSelect
                className={css.halfField}
                id={formId ? `${formId}.stateRegion` : 'stateRegion'}
                name="stateRegion"
                label="State / Region"
                validate={validators.required('Please select a state')}
              >
                <option value="">Select state…</option>
                {US_STATES.map(s => (
                  <option key={s.abbr} value={s.abbr}>{s.name} ({s.abbr})</option>
                ))}
              </FieldSelect>
            </div>

            <div className={css.addressRow}>
              <FieldTextInput
                className={css.halfField}
                type="text"
                id={formId ? `${formId}.zipCode` : 'zipCode'}
                name="zipCode"
                autoComplete="postal-code"
                label="Zip / Postal Code"
                placeholder=""
                validate={validators.required('Zip code is required')}
              />
              <FieldTextInput
                className={css.halfField}
                type="text"
                id={formId ? `${formId}.country` : 'country'}
                name="country"
                autoComplete="country-name"
                label="Country"
                placeholder=""
                disabled
              />
            </div>

            <FieldTextInput
              className={css.row}
              type="tel"
              id={formId ? `${formId}.phoneNumber` : 'phoneNumber'}
              name="phoneNumber"
              autoComplete="tel"
              label="Phone Number"
              placeholder=""
              validate={validators.required('Phone number is required')}
            />

            {showCustomUserFields ? (
              <div className={css.customFields}>
                {userFieldProps.map(({ key, ...fieldProps }) => (
                  <CustomExtendedDataField key={key} {...fieldProps} formId={formId} />
                ))}
              </div>
            ) : null}

            <div className={css.termsWrapper}>{termsAndConditions}</div>

            {isPasswordUsedMoreThanOnce(values) ? (
              <div className={css.error}>
                <FormattedMessage id="SignupForm.passwordRepeatedOnOtherFields" />
              </div>
            ) : null}

            <div className={css.step2Buttons}>
              <button
                type="button"
                className={css.backBtn}
                onClick={() => {
                  setStep(1);
                  window.scrollTo(0, 0);
                }}
              >
                Back
              </button>
              <PrimaryButton type="submit" inProgress={submitInProgress} disabled={submitDisabled}>
                Create Account
              </PrimaryButton>
            </div>
          </Form>
        );
      }}
    />
  );
};

/**
 * A component that renders the 2-step signup form.
 *
 * @component
 */
const SignupForm = props => {
  const intl = useIntl();
  return <SignupFormComponent {...props} intl={intl} />;
};

export default SignupForm;
